import { forwardRef } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    error?: string;
    label?: string;
    showCharCount?: boolean;
}

/**
 * Input component following BottleShop design system
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ error, label, className = '', id, showCharCount, maxLength, value, ...props }, ref) => {
        const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
        const baseStyles =
            'w-full h-10 px-4 text-sm bg-[#f8fafc] border border-[#d1d5db] rounded-lg transition-all duration-150';
        const focusStyles =
            'focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-[#6e64f0]';
        const errorStyles = error ? 'border-[#ef4444] focus:ring-[#ef4444]' : '';
        const disabledStyles = 'disabled:opacity-50 disabled:cursor-not-allowed';

        const currentLength = value ? String(value).length : 0;
        const showCounter = showCharCount && maxLength;

        return (
            <div className="w-full">
                {label && (
                    <div className="flex items-center justify-between mb-2">
                        <label
                            htmlFor={inputId}
                            className="block text-sm font-medium text-[#1e293b]"
                        >
                            {label}
                        </label>
                        {showCounter && (
                            <span
                                className={`text-xs ${currentLength > maxLength ? 'text-[#ef4444]' : 'text-[#64748b]'}`}
                            >
                                {currentLength}/{maxLength}
                            </span>
                        )}
                    </div>
                )}
                <input
                    ref={ref}
                    id={inputId}
                    maxLength={maxLength}
                    value={value}
                    className={`${baseStyles} ${focusStyles} ${errorStyles} ${disabledStyles} ${className}`}
                    {...props}
                />
                {error && <p className="mt-1 text-xs text-[#ef4444]">{error}</p>}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;
